import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, NgZone, OnInit, QueryList, ViewChildren } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IBreadcrumbs } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import { catchError, combineLatest, Subject, take, takeUntil, tap } from 'rxjs';
import { IFormResource } from '../../../shared/components/form/form.component';
import { IonContent } from "@ionic/angular/standalone";
import { ActivatedRoute, RouterModule } from '@angular/router';
import { AlertsService, TimestampFilter } from '../../../core/services/alerts.service';
import { MatExpansionModule } from '@angular/material/expansion';
import { EventTypeCodes, eventTypeToData, INotificationData, canDisplayAlertEventType } from '../../../shared/models/be/notification';
import 'chartjs-adapter-luxon';
import { MatGridListModule } from '@angular/material/grid-list';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ICtaOutput, IOptionOutput, ITableColumns, ITableConfigParams, ITableConfigParamsFilter, ITableData, TableComponent } from '../../../shared/components/table/table.component';
import moment, { Moment } from 'moment';
import { ToastService } from '../../../core/services/utils/toast.service';
import { IonicColors } from '../../../shared/enums/ionicColors.enum';
import { FiltersList } from '../../../shared/components/table/filtersList';
import { FacilitiesService } from '../../../core/services/facilities.service';
import { TableService } from '../../../shared/components/table/table.service';
import { IUnit } from '../../../shared/models/be/unit';
import { IUser, UserRole } from '../../../shared/models/be/user';
import { IUserTableFilterPreference } from '../../../shared/models/be/userPreferences';
import { SharedService } from '../../../core/services/shared.service';
import { RequestsService } from '../../../core/services/utils/requests.service';
import { MatDialog } from '@angular/material/dialog';
import { AlertVideoDialogComponent } from '../../../shared/components/alertVideoDialog/alert-video-dialog.component';
import { GuideService } from '../../../core/services/guide.service';
import { HistoryTutorialExampleComponent } from '../../../shared/components/history-tutorial-example/history-tutorial-example.component';

@Component({
  selector: 'app-settings',
  templateUrl: './alertsHistory.page.html',
  styleUrls: ['./alertsHistory.page.scss'],
  imports: [
    IonContent,
    CommonModule,
    TranslateModule,
    RouterModule,
    MatExpansionModule,
    MatGridListModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    TableComponent,
    HistoryTutorialExampleComponent
  ]
})
export class AlertsHistoryPage implements OnInit {
  @ViewChildren('chartCanvas') canvasRefs!: QueryList<ElementRef<HTMLCanvasElement>>;
  public breadcrumbsList: IBreadcrumbs[] = [{
    icon: 'query_stats',
    label: 'ALERTS_HISTORY'
  }]

  public tableRowSpan: number = 11;
  public isDataLoading: boolean = false;
  public resource?: IFormResource & { unitId: string; buildingId: string };
  public data: {
    id: string;
    name: string;
    alerts: INotificationData[];
    chartData?: any;
    isLoaded?: boolean;
  }[] = [];
  private currentUser: IUser | null = null;

  public tableColumns: ITableColumns[] = [
    { label: 'ALERT_DATE', id: 'alertDate', sortBy: 'alertDateTimestamp', showCol: true },
    { label: 'MANAGED_AT', id: 'managedAt', sortBy: 'managedAtTimestamp', showCol: true },
    { label: 'SEVERITY', id: 'severity', showCol: true },
    { label: 'BUILDING', id: 'building', showCol: false },
    { label: 'UNIT', id: 'unit', showCol: true },
    { label: 'ROOM', id: 'room', showCol: true },
    { label: 'BED', id: 'bed', showCol: true },
    { label: 'MANAGED_BY', id: 'managedBy', showCol: true },
    { label: 'DEVICE', id: 'device', showCol: false },
  ];
  public tableData: ITableData[] = [];
  private destroy$ = new Subject<void>();
  public tableConfigParams: ITableConfigParams = {
    id: "alertsHistory",
    referenceLabel: 'ALERTS_HISTORY',
    searchBy: [],
    hideAddBtn: true,
    filters: []
  };
  public rawData: INotificationData[] = [];
  private readonly defaultTimestampFilter: TimestampFilter = 'last24h';

  constructor(
    private alertService: AlertsService,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private toastService: ToastService,
    private filtersList: FiltersList,
    private facilitiesService: FacilitiesService,
    private sharedSrv: SharedService,
    private cdRef: ChangeDetectorRef,
    private zone: NgZone,
    private tableService: TableService,
    private requestsService: RequestsService,
    private dialog: MatDialog,
    public guide: GuideService
  ) {
    this.currentUser = requestsService.getCurrentUser();
    if (this.currentUser?.role === 'superuser') {
      this.tableColumns.unshift({ label: 'ID', id: 'id', showCol: true });
      this.tableColumns.push({ label: 'NOTIFICATION_TITLE', id: 'title', showCol: true, plainText: true },
        { label: 'NOTIFICATION_BODY', id: 'body', showCol: true, plainText: true });
    }
  }

  ngOnInit() {
    this.refreshFiltersFromState();
    this.loadAlerts();
    this.sharedSrv.newAlert$.pipe(takeUntil(this.destroy$)).subscribe((alert) => {
      alert && canDisplayAlertEventType(alert.eventType, this.requestsService.getCurrentUser()?.role)
        && !this.rawData.some((current) => current.id === alert.id) && this.zone.run(() => {
        this.rawData.push(alert);
        this.tableData = this.alertsToTableData(this.rawData);
        this.cdRef.detectChanges(); // Forza l'aggiornamento della vista
      });
    });
    this.sharedSrv.managedAlert$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((alert) => {
      let elementIndex = this.rawData.findIndex((data) => data.id === alert?.id);
      if (alert && elementIndex !== -1 && !alert.managedAt) {
        this.loadAlerts(false, true);
        return;
      }
      alert && elementIndex !== -1 && this.zone.run(() => {
        this.rawData[elementIndex] = alert;
        this.tableData = this.alertsToTableData(this.rawData);
        this.cdRef.detectChanges(); // Forza l'aggiornamento della vista
      });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    this.resource = this.route.snapshot.data['data'];
  }

  /**
   * @description Funzione che va ad aggiornare i dati della table in pagina
   */
  loadAlerts(refreshData: boolean = false, reloadOnlyOnStatusChange: boolean = false) {
    const currentSelectedTimestampFilter = this.getSelectedTimestampFilter();
    if (refreshData && this.isDataLoading) {
      return;
    }
    this.isDataLoading = !reloadOnlyOnStatusChange && true;
    combineLatest([
      this.alertService.getAnalyticsAlerts$(currentSelectedTimestampFilter),
      this.facilitiesService.getUnits$()
    ]).pipe(
      take(1),
      tap(([alerts, units]) => {
        this.rawData = alerts.filter((alert) => canDisplayAlertEventType(alert.eventType, this.requestsService.getCurrentUser()?.role));
        this.loadFiltersData(units);
        this.tableData = this.alertsToTableData(this.rawData);
        this.isDataLoading = false;
        refreshData && this.toastService.showToast(this.translateService.instant('GENERAL.SUCCESS.DATA_REFRESHED'), IonicColors.GREEN);
      }),
      catchError(err => {
        this.isDataLoading = false;
        refreshData && this.toastService.showToast(this.translateService.instant('GENERAL.ERRORS.LOADING_DATA'), IonicColors.RED);
        throw err
      })
    ).subscribe()
  }

  private refreshFiltersFromState(): void {
    const savedFilters = this.getSavedFilters();
    this.tableConfigParams.filters = this.buildDefaultFilters().map((defaultFilter) => {
      const savedFilter = savedFilters.find((filter) => filter.id === defaultFilter.id);
      if (!savedFilter) {
        return defaultFilter;
      }

      return {
        ...defaultFilter,
        selectedValue: this.cloneSelectedValue(savedFilter.selectedValue),
        startDate: savedFilter.startDate ? new Date(savedFilter.startDate) : defaultFilter.startDate,
        endDate: savedFilter.endDate ? new Date(savedFilter.endDate) : defaultFilter.endDate
      };
    });
  }

  private buildDefaultFilters(): ITableConfigParamsFilter[] {
    const roomFilter = this.filtersList.getFilterById('room');
    const unitFilter = this.filtersList.getFilterById('unit');
    const severityFilter = this.filtersList.getFilterById('severity');
    if (severityFilter) {
      const eventTypes: EventTypeCodes[] = ['01', '02', '03', '04', '05', '10', '06', '07', '08'];
      severityFilter.options = eventTypes
        .filter((eventType) => canDisplayAlertEventType(eventType, this.currentUser?.role))
        .map((eventType) => ({
          id: eventType,
          name: this.translateService.instant(eventTypeToData[eventType].translation),
          translateName: false
        }));
      severityFilter.selectedValue = severityFilter.options.map((option) => option.id);
    }

    return [
      severityFilter,
      unitFilter ? { ...unitFilter, defaultValue: 'all' } : null,
      roomFilter ? { ...roomFilter, refreshIfPopulated: true } : null,
      this.filtersList.getFilterById('timestamp'),
      this.filtersList.getFilterById('managedBy')
    ].filter((filter) => Boolean(filter)) as ITableConfigParamsFilter[];
  }

  private getSavedFilters(): IUserTableFilterPreference[] {
    return this.tableService.getTablePreferences(this.tableConfigParams.id).filters || [];
  }

  private getSelectedTimestampFilter(): TimestampFilter {
    const selectedValue = this.tableConfigParams.filters?.find((filter) => filter.id === 'timestamp')?.selectedValue;
    return this.isTimestampFilter(selectedValue) ? selectedValue : this.defaultTimestampFilter;
  }

  private cloneSelectedValue(selectedValue: ITableConfigParamsFilter['selectedValue']): ITableConfigParamsFilter['selectedValue'] {
    if (this.isStringArraySelectedValue(selectedValue)) {
      return [...selectedValue];
    }

    if (this.isObjectArraySelectedValue(selectedValue)) {
      return selectedValue.map((value) => ({ ...value }));
    }

    if (selectedValue instanceof Date) {
      return new Date(selectedValue);
    }

    return selectedValue;
  }

  private isTimestampFilter(selectedValue: ITableConfigParamsFilter['selectedValue']): selectedValue is TimestampFilter {
    return typeof selectedValue === 'string' && ['last6h', 'last24h', 'lastWeek', 'lastMonth'].includes(selectedValue);
  }

  private isStringArraySelectedValue(selectedValue: ITableConfigParamsFilter['selectedValue']): selectedValue is string[] {
    return Array.isArray(selectedValue) && selectedValue.every((value) => typeof value === 'string');
  }

  private isObjectArraySelectedValue(
    selectedValue: ITableConfigParamsFilter['selectedValue']
  ): selectedValue is { id: string; value?: string | Moment }[] {
    return Array.isArray(selectedValue) && selectedValue.every((value) => typeof value === 'object' && value !== null);
  }

  alertsToTableData(alerts: INotificationData[]): ITableData[] {
    const sortedData = alerts.filter((alert) => canDisplayAlertEventType(alert.eventType, this.requestsService.getCurrentUser()?.role)).sort((a, b) => {
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);
      return dateB.getTime() - dateA.getTime(); // Ordina dal più recente al meno recente
    });

    return sortedData.map((alert) => ({
      data: {
        id: alert.id,
        title: alert.eventType === '08' ? alert.title : undefined,
        body: alert.eventType === '08' ? alert.body : undefined,
        timestamp: null,
        alertDateTimestamp: this.toTimestamp(alert.timestamp),
        managedAtTimestamp: alert.managedAt ? this.toTimestamp(alert.managedAt) : null,
        alertDate: moment(alert.timestamp).format("DD/MM - HH:mm"),
        managedAt: alert.managedAt ? moment(alert.managedAt).format("DD/MM - HH:mm") : undefined,
        building: alert.facilities.building.name,
        unit: alert.facilities.unit.name,
        room: alert.facilities.room.name,
        bed: alert.bed_name_mentorage || "-",
        managedBy: this.roleToText(alert.managed_by_info?.role),
        severity: this.translateService.instant(eventTypeToData[alert.eventType]?.translation),
        device: alert.deviceId
      },
      options: [
        ...(this.currentUser?.role === 'superuser' ? [{
          action: 'delete_alert',
          icon: 'delete',
          label: 'DELETE_ALERT'
        }, {
          action: 'view_video',
          icon: 'movie',
          label: 'VIEW_ALERT_VIDEO'
        }] : [])
      ].filter((x) => x),
      rowStyle: {
        bgColor: this.eventTypeToBgColor(alert.eventType)
      },
    } as ITableData)
    );
  }

  roleToText(role: UserRole) {
    if ((role as any) === 'system') {
      return "Autogestione"
    } else if (role === 'superuser') {
      return "Amministratore"
    } else if (role) {
      return "Utente"
    } else {
      return "-"
    }
  }

  eventTypeToBgColor(eventType: EventTypeCodes) {
    switch (eventType) {
      case '01':
        return 'rgb(238 134 125 / 15%)'
      case '02':
        return 'rgb(231 156 83 / 10%)'
      case '03':
        return 'rgb(231 156 83 / 10%)'
      case '04':
        return 'rgb(231 218 83 / 5%)'
      case '05':
        return 'rgb(231 218 83 / 5%)'
      case '10':
        return 'rgb(231 218 83 / 5%)'
      case '06':
        return '#8080801f'
      case '07':
        return '#00800021'
      default:
        return 'unset'
    }
  }

  loadFiltersData(units: IUnit[]) {
    const unitsFilter = this.tableConfigParams.filters?.find((filter) => filter.id === 'unit');
    const roomFilter = this.tableConfigParams.filters?.find((filter) => filter.id === 'room');
    if (unitsFilter) {
      unitsFilter.dontResetOnClear = true;
      const unitOptions = units.map((unit) => ({ id: unit.id!, name: unit.name! }));
      const selectedUnit = unitsFilter.selectedValue;
      const savedUnit = this.getSavedFilters().find((filter) => filter.id === 'unit');
      const savedSelectedUnit = savedUnit?.selectedValue;
      const hasValidSavedSelection = !savedUnit || (typeof savedSelectedUnit === 'string' && (savedSelectedUnit === 'all' || unitOptions.some((unit) => unit.id === savedSelectedUnit)));
      const hasValidSelection = typeof selectedUnit === 'string'
        && (selectedUnit === 'all' || unitOptions.some((unit) => unit.id === selectedUnit));

      unitsFilter.options = [
        { id: 'all', name: this.translateService.instant('TABLE.FILTER.LABEL.ALL') },
        ...unitOptions
      ];

      if (!hasValidSelection) {
        unitsFilter.selectedValue = 'all';
      }
      if (!hasValidSavedSelection) {
        roomFilter && (roomFilter.selectedValue = undefined);
        roomFilter && (roomFilter.options = []);
        this.tableService.clearTableFilters(this.tableConfigParams.id, ['unit', 'room']);
      }
    }
  }

  loadFilters() {
    !this.tableConfigParams.filters?.length && (this.tableConfigParams.filters = this.buildDefaultFilters())
  };

  onFilterSelection(filter: ITableConfigParamsFilter) {
    if (filter.id === 'timestamp') {
      this.loadAlerts();
    }
  }

  private toTimestamp(value: Date | string): number {
    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  /**
   * @description Funzione invocata al click di una opzione della table
   * @param event Viene passato un oggetto contenente l'id delle risorsa in oggetto e l'action invocata
   */
  async onOptionAction(event: IOptionOutput) {
    switch (event.action) {
      case "delete_alert":
        this.alertService.deleteAlert$(event.resource.id).pipe(
          tap(() => {
            this.toastService.showToast(this.translateService.instant('GENERAL.SUCCESS.ALERT_DELETED'), IonicColors.GREEN);
            this.loadAlerts();
          }),
          catchError((err) => {
            this.toastService.showToast(this.translateService.instant('GENERAL.ERRORS.ALERT_DELETE_ERROR'), IonicColors.RED);
            throw err;
          })
        ).subscribe()
        break;
      case "view_video":
        if (!event.resource.id) {
          this.toastService.showToast('Impossibile individuare la notifica selezionata.', IonicColors.RED);
          return;
        }

        this.dialog.open(AlertVideoDialogComponent, {
          width: 'min(920px, 95vw)',
          maxWidth: '95vw',
          maxHeight: '90vh',
          data: {
            notificationId: String(event.resource.id),
            title: this.translateService.instant('VIEW_ALERT_VIDEO')
          }
        });
        break;
    }
  }

  /**
   * @description Funzione invocata al click di un button (Aggiungi risorsa, refresh dati, ecc...)
   * @param event Viene passato un oggetto contenente il tipo di action invocata
   */
  onCtaAction(event: ICtaOutput) {
    switch (event.action) {
      case 'refresh':
        this.loadAlerts(true);
        break;
      default:
        break;
    }
  }
}
